import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Plug, Users } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { queries } from "@/lib/queries";
import { DashboardPanel } from "./relatorios";
import { useState } from "react";

export const Route = createFileRoute("/app/$workspaceId/")({
  component: VisaoGeralPage,
});

function ClienteFinalHome({ workspaceId, name }: { workspaceId: string; name: string }) {
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  return (
    <>
      <PageHeader
        title={name}
        description="Seus relatórios de anúncios: métricas, comparação com o período anterior, campanhas, criativos e quebras."
      />
      <DashboardPanel
        workspaceId={workspaceId}
        clientId={clientId}
        onClientChange={setClientId}
        canExport
      />
    </>
  );
}

function VisaoGeralPage() {
  const { workspaceId } = Route.useParams();
  const workspace = useQuery(queries.workspace(workspaceId));
  const members = useQuery(queries.members(workspaceId));
  const meta = useQuery(queries.metaConnections(workspaceId));

  if (workspace.isPending) return <LoadingState rows={4} />;
  if (workspace.isError)
    return <ErrorState error={workspace.error} onRetry={() => void workspace.refetch()} />;

  // Cliente final: ao entrar, vê o dashboard dos clientes a que está vinculado (Fase 6).
  if (workspace.data.role === "cliente_final") {
    return <ClienteFinalHome workspaceId={workspaceId} name={workspace.data.name} />;
  }

  return (
    <>
      <PageHeader title={workspace.data.name} description="Visão geral do workspace." />
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Conta Meta</CardTitle>
            <CardDescription>
              Conecte uma conta para importar Business Managers, contas de anúncio e páginas.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {meta.isPending ? (
              <LoadingState rows={2} />
            ) : meta.isError ? (
              <ErrorState error={meta.error} onRetry={() => void meta.refetch()} />
            ) : meta.data.length === 0 ? (
              <EmptyState
                icon={<Plug className="size-8" aria-hidden />}
                title="Nenhuma conta Meta conectada"
                description="Conecte um perfil Meta para importar os Business Managers, contas, páginas e pixels."
                action={
                  <Button asChild variant="outline">
                    <Link to="/app/$workspaceId/integracoes" params={{ workspaceId }}>
                      Ir para Integrações
                    </Link>
                  </Button>
                }
              />
            ) : (
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Plug className="size-8 text-muted-foreground" aria-hidden />
                  <div>
                    <p className="text-2xl font-semibold">
                      {meta.data.reduce((n, c) => n + c.counts.adAccounts, 0)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      contas de anúncio em {meta.data.length}{" "}
                      {meta.data.length === 1 ? "conexão" : "conexões"}
                      {meta.data.some((c) => c.status !== "ativa")
                        ? " · há conexão para reconectar"
                        : ""}
                    </p>
                  </div>
                </div>
                <Button asChild variant="outline">
                  <Link to="/app/$workspaceId/contas-de-anuncio" params={{ workspaceId }}>
                    Ver contas
                  </Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Equipe</CardTitle>
            <CardDescription>Quem tem acesso a este workspace.</CardDescription>
          </CardHeader>
          <CardContent>
            {members.isPending ? (
              <LoadingState rows={2} />
            ) : members.isError ? (
              <ErrorState error={members.error} onRetry={() => void members.refetch()} />
            ) : (
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Users className="size-8 text-muted-foreground" aria-hidden />
                  <div>
                    <p className="text-2xl font-semibold">{members.data.length}</p>
                    <p className="text-sm text-muted-foreground">
                      {members.data.length === 1 ? "membro" : "membros"}
                    </p>
                  </div>
                </div>
                <Button asChild variant="outline">
                  <Link to="/app/$workspaceId/membros" params={{ workspaceId }}>
                    Gerenciar
                  </Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
